import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { AdminAuthProvider, useAdminAuth } from "@/lib/adminAuth";
import { Toaster } from "sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AdminLogin from "@/pages/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import KycQueue from "@/pages/admin/KycQueue";
import KycDetail from "@/pages/admin/KycDetail";
import ActivityLog from "@/pages/admin/ActivityLog";
import Users from "@/pages/admin/Users";
import UserDetail from "@/pages/admin/UserDetail";
import Parcels from "@/pages/admin/Parcels";
import AdminTrips from "@/pages/admin/Trips";
import ParcelPage from "@/pages/ParcelPage";
import Notifications from "@/pages/Notifications";
import ParcelDetail from "@/pages/admin/ParcelDetail";
import Placeholder from "@/pages/admin/Placeholder";
import PricingConfig from "@/pages/admin/PricingConfig";
import Payouts from "@/pages/admin/Payouts";
import { DisputesList, DisputeDetail } from "@/pages/admin/Disputes";
import TestData from "@/pages/admin/TestData";
import Cancellations from "@/pages/admin/Cancellations";
import ChatReports from "@/pages/admin/ChatReports";
import CodOutstanding from "@/pages/admin/CodOutstanding";
import Home from "@/pages/Home";
import DeliveryDetail from "@/pages/DeliveryDetail";
import PublicTrack from "@/pages/PublicTrack";
import Terms from "@/pages/legal/Terms";
import Privacy from "@/pages/legal/Privacy";
import RefundPolicy from "@/pages/legal/RefundPolicy";
import DeliveryPolicy from "@/pages/legal/DeliveryPolicy";
import ProhibitedItems from "@/pages/legal/ProhibitedItems";
import Protection from "@/pages/legal/Protection";
import CarrierAgreement from "@/pages/legal/CarrierAgreement";
import Grievance from "@/pages/legal/Grievance";
import Contact from "@/pages/legal/Contact";
import "@/App.css";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const AdminProtected = ({ children }) => {
  const { admin, loading } = useAdminAuth();
  if (loading) return <div className="min-h-screen grid place-items-center bg-slate-950 text-slate-500 text-sm">Loading…</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <AdminAuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors />
          <Routes>
            {/* Public + customer */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/track/:token" element={<PublicTrack />} />
            <Route path="/app" element={<Protected><Home /></Protected>} />
            <Route path="/delivery/:id" element={<Protected><DeliveryDetail /></Protected>} />
            <Route path="/parcel/:id" element={<Protected><ParcelPage /></Protected>} />
            <Route path="/parcel/:id/browse" element={<Protected><ParcelPage browse /></Protected>} />
            <Route path="/notifications" element={<Protected><Notifications /></Protected>} />

            {/* Legal */}
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/delivery-policy" element={<DeliveryPolicy />} />
            <Route path="/prohibited-items" element={<ProhibitedItems />} />
            <Route path="/protection" element={<Protection />} />
            <Route path="/carrier-agreement" element={<CarrierAgreement />} />
            <Route path="/grievance" element={<Grievance />} />
            <Route path="/contact" element={<Contact />} />

            {/* Admin — separate auth */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminProtected><AdminLayout /></AdminProtected>}>
              <Route index element={<Dashboard />} />
              <Route path="kyc" element={<KycQueue />} />
              <Route path="kyc/:uid" element={<KycDetail />} />
              <Route path="users" element={<Users />} />
              <Route path="users/:uid" element={<UserDetail />} />
              <Route path="parcels" element={<Parcels />} />
              <Route path="parcels/:pid" element={<ParcelDetail />} />
              <Route path="trips" element={<AdminTrips />} />
              <Route path="payouts" element={<Payouts />} />
              <Route path="disputes" element={<DisputesList />} />
              <Route path="disputes/:did" element={<DisputeDetail />} />
              <Route path="pricing" element={<PricingConfig />} />
              <Route path="test-data" element={<TestData />} />
              <Route path="cancellations" element={<Cancellations />} />
              <Route path="chat-reports" element={<ChatReports />} />
              <Route path="cod-outstanding" element={<CodOutstanding />} />
              <Route path="activity" element={<ActivityLog />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AdminAuthProvider>
    </AuthProvider>
  );
}

export default App;
